import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { RequireAuth, RequireSuperAdmin } from './context/AuthContext'
import { LandingPage } from './pages/LandingPage'

const AuthShell = lazy(() =>
  import('./components/AuthShell').then((m) => ({ default: m.AuthShell })),
)

const AppLayout = lazy(() =>
  import('./components/layout/AppLayout').then((m) => ({ default: m.AppLayout })),
)
const AppProvider = lazy(() =>
  import('./context/AppContext').then((m) => ({ default: m.AppProvider })),
)
const OnboardingProvider = lazy(() =>
  import('./context/OnboardingContext').then((m) => ({ default: m.OnboardingProvider })),
)

const PricingPage = lazy(() =>
  import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })),
)
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage })),
)
const UptimeMonitoringPage = lazy(() =>
  import('./pages/UptimeMonitoringPage').then((m) => ({ default: m.UptimeMonitoringPage })),
)
const SlackUptimeAlertsPage = lazy(() =>
  import('./pages/SlackUptimeAlertsPage').then((m) => ({ default: m.SlackUptimeAlertsPage })),
)
const EmailUptimeAlertsPage = lazy(() =>
  import('./pages/EmailUptimeAlertsPage').then((m) => ({ default: m.EmailUptimeAlertsPage })),
)
const PublicStatusPagesPage = lazy(() =>
  import('./pages/PublicStatusPagesPage').then((m) => ({ default: m.PublicStatusPagesPage })),
)
const SslDomainMonitoringPage = lazy(() =>
  import('./pages/SslDomainMonitoringPage').then((m) => ({ default: m.SslDomainMonitoringPage })),
)
const AlternativesPage = lazy(() =>
  import('./pages/AlternativesPage').then((m) => ({ default: m.AlternativesPage })),
)
const LoginPage = lazy(() =>
  import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })),
)
const SignupPage = lazy(() =>
  import('./pages/SignupPage').then((m) => ({ default: m.SignupPage })),
)
const AuthConfirmPage = lazy(() =>
  import('./pages/AuthConfirmPage').then((m) => ({ default: m.AuthConfirmPage })),
)
const PublicStatusPage = lazy(() =>
  import('./pages/PublicStatusPage').then((m) => ({ default: m.PublicStatusPage })),
)
const TermsPage = lazy(() =>
  import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })),
)
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
)
const RefundPage = lazy(() =>
  import('./pages/RefundPage').then((m) => ({ default: m.RefundPage })),
)
const CookiesPage = lazy(() =>
  import('./pages/CookiesPage').then((m) => ({ default: m.CookiesPage })),
)
const AdminDashboardPage = lazy(() =>
  import('./pages/admin/AdminDashboardPage').then((m) => ({
    default: m.AdminDashboardPage,
  })),
)
const AdminOrgsPage = lazy(() =>
  import('./pages/admin/AdminOrgsPage').then((m) => ({ default: m.AdminOrgsPage })),
)
const AdminOrgDetailPage = lazy(() =>
  import('./pages/admin/AdminOrgDetailPage').then((m) => ({
    default: m.AdminOrgDetailPage,
  })),
)
const AdminPlansPage = lazy(() =>
  import('./pages/admin/AdminPlansPage').then((m) => ({ default: m.AdminPlansPage })),
)
const StatusPage = lazy(() =>
  import('./pages/StatusPage').then((m) => ({ default: m.StatusPage })),
)
const StatusPageCreatePage = lazy(() =>
  import('./pages/StatusPageCreatePage').then((m) => ({ default: m.StatusPageCreatePage })),
)
const StatusPageEditPage = lazy(() =>
  import('./pages/StatusPageEditPage').then((m) => ({ default: m.StatusPageEditPage })),
)
const MonitorsPage = lazy(() =>
  import('./pages/MonitorsPage').then((m) => ({ default: m.MonitorsPage })),
)
const MonitorCreatePage = lazy(() =>
  import('./pages/MonitorCreatePage').then((m) => ({ default: m.MonitorCreatePage })),
)
const MonitorEditPage = lazy(() =>
  import('./pages/MonitorEditPage').then((m) => ({ default: m.MonitorEditPage })),
)
const MonitorLogPage = lazy(() =>
  import('./pages/MonitorLogPage').then((m) => ({ default: m.MonitorLogPage })),
)
const EscalationPoliciesPage = lazy(() =>
  import('./pages/EscalationPoliciesPage').then((m) => ({ default: m.EscalationPoliciesPage })),
)
const EscalationPolicyCreatePage = lazy(() =>
  import('./pages/EscalationPolicyCreatePage').then((m) => ({
    default: m.EscalationPolicyCreatePage,
  })),
)
const EscalationPolicyEditPage = lazy(() =>
  import('./pages/EscalationPolicyEditPage').then((m) => ({
    default: m.EscalationPolicyEditPage,
  })),
)
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
)

function RouteFallback() {
  return (
    <div
      className="flex min-h-[40vh] items-center justify-center bg-background text-sm text-text-muted"
      aria-busy="true"
      aria-label="Loading page"
    >
      Loading…
    </div>
  )
}

function LazyPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>
}

function AppDataShell() {
  return (
    <LazyPage>
      <AppProvider>
        <OnboardingProvider />
      </AppProvider>
    </LazyPage>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />

        <Route
          path="/pricing"
          element={
            <LazyPage>
              <PricingPage />
            </LazyPage>
          }
        />
        <Route
          path="/contact"
          element={
            <LazyPage>
              <ContactPage />
            </LazyPage>
          }
        />
        <Route
          path="/uptime-monitoring"
          element={
            <LazyPage>
              <UptimeMonitoringPage />
            </LazyPage>
          }
        />
        <Route
          path="/slack-uptime-alerts"
          element={
            <LazyPage>
              <SlackUptimeAlertsPage />
            </LazyPage>
          }
        />
        <Route
          path="/email-uptime-alerts"
          element={
            <LazyPage>
              <EmailUptimeAlertsPage />
            </LazyPage>
          }
        />
        <Route
          path="/public-status-pages"
          element={
            <LazyPage>
              <PublicStatusPagesPage />
            </LazyPage>
          }
        />
        <Route
          path="/ssl-domain-monitoring"
          element={
            <LazyPage>
              <SslDomainMonitoringPage />
            </LazyPage>
          }
        />
        <Route
          path="/alternatives"
          element={
            <LazyPage>
              <AlternativesPage />
            </LazyPage>
          }
        />
        <Route
          path="/status/:slug"
          element={
            <LazyPage>
              <PublicStatusPage />
            </LazyPage>
          }
        />
        <Route
          path="/terms"
          element={
            <LazyPage>
              <TermsPage />
            </LazyPage>
          }
        />
        <Route
          path="/privacy"
          element={
            <LazyPage>
              <PrivacyPage />
            </LazyPage>
          }
        />
        <Route
          path="/refund"
          element={
            <LazyPage>
              <RefundPage />
            </LazyPage>
          }
        />
        <Route
          path="/cookies"
          element={
            <LazyPage>
              <CookiesPage />
            </LazyPage>
          }
        />

        <Route
          element={
            <LazyPage>
              <AuthShell />
            </LazyPage>
          }
        >
          <Route
            path="/login"
            element={
              <LazyPage>
                <LoginPage />
              </LazyPage>
            }
          />
          <Route
            path="/signup"
            element={
              <LazyPage>
                <SignupPage />
              </LazyPage>
            }
          />
          <Route
            path="/auth/confirm"
            element={
              <LazyPage>
                <AuthConfirmPage />
              </LazyPage>
            }
          />

          <Route element={<RequireAuth />}>
            <Route
              element={
                <LazyPage>
                  <AppLayout />
                </LazyPage>
              }
            >
              <Route element={<RequireSuperAdmin />}>
                <Route
                  path="admin"
                  element={
                    <LazyPage>
                      <AdminDashboardPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="admin/orgs"
                  element={
                    <LazyPage>
                      <AdminOrgsPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="admin/orgs/:id"
                  element={
                    <LazyPage>
                      <AdminOrgDetailPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="admin/plans"
                  element={
                    <LazyPage>
                      <AdminPlansPage />
                    </LazyPage>
                  }
                />
              </Route>

              <Route element={<AppDataShell />}>
                <Route
                  path="status"
                  element={
                    <LazyPage>
                      <StatusPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="status-pages"
                  element={<Navigate to="/status?view=public" replace />}
                />
                <Route
                  path="status-pages/new"
                  element={
                    <LazyPage>
                      <StatusPageCreatePage />
                    </LazyPage>
                  }
                />
                <Route
                  path="status-pages/:id"
                  element={
                    <LazyPage>
                      <StatusPageEditPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="monitors"
                  element={
                    <LazyPage>
                      <MonitorsPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="monitors/new"
                  element={
                    <LazyPage>
                      <MonitorCreatePage />
                    </LazyPage>
                  }
                />
                <Route
                  path="monitors/:id/log"
                  element={
                    <LazyPage>
                      <MonitorLogPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="monitors/:id"
                  element={
                    <LazyPage>
                      <MonitorEditPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="escalation-policies"
                  element={
                    <LazyPage>
                      <EscalationPoliciesPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="escalation-policies/new"
                  element={
                    <LazyPage>
                      <EscalationPolicyCreatePage />
                    </LazyPage>
                  }
                />
                <Route
                  path="escalation-policies/:id"
                  element={
                    <LazyPage>
                      <EscalationPolicyEditPage />
                    </LazyPage>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <LazyPage>
                      <SettingsPage />
                    </LazyPage>
                  }
                />
              </Route>

              <Route
                path="*"
                element={
                  <LazyPage>
                    <NotFoundPage />
                  </LazyPage>
                }
              />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
